// What the worker says, in the page's words (unify-ui ship 3, ruling R9). The worker speaks codes: an issue or an error
// is DATA — `{ code, face, elId, …params }` — and this module turns it into the sentence the page shows, through the
// page's own table (`S.worker`, src/strings/<lang>.js). It is the one place a worker reply gains words: every reply
// passes through it in main.js `call()`, so everything downstream (the issue list, the download note, the status line,
// the way-out button) reads `.message` / `action.label` as before.
//
// Params a code carries (src/validate.js, src/templates.js, src/generate.js, src/sanitize.js, src/geometry.worker.js):
//   subject  { type: 'text', text } | { type: 'icon', iconId }   the element an issue is about
//   chars    the characters the font lacks (missing-chars)     kind, point   bad-char ('control', named by its code point
//                                                              'U+0009' | 'leading': a floating Thai vowel or tone mark)
//   max      too-many's limit       mm   too-thin-tag's thickness       factor   too-thin on the back
//   fit      true: a template's fit found the element too thin (the back reads "too small to engrave")
//   way      'phone' | 'plain': the way out a template names (no-fit, too-thin from a fit)
// A code this module does not know keeps the message it came with (`internal`, `bad-format`: a developer's words).
const own = (o, k) => o != null && Object.hasOwn(o, k);

// what an issue calls its element in a sentence: the role's word (name, phone) or the kind's (text, icon)
// (only the roles a template names have a word of their own: an added element is named by its kind, whatever its id)
const ROLES = ['name', 'phone', 'icon'];
const labelOf = (R, issue) => (ROLES.includes(issue.elId) ? R[issue.elId] : issue.subject?.type === 'icon' ? R.icon : R.text);
// how an issue quotes its element: the text as typed, or the icon's name
const quoteOf = (W, S, subject) => (subject?.type === 'icon' ? W.quoteIcon(own(S.icon, subject.iconId) ? S.icon[subject.iconId] : '') : W.quoteText(subject?.text ?? ''));

export function sentence(issue, S) {
  const W = S.worker, R = S.roleLabel;
  const q = () => quoteOf(W, S, issue.subject);
  const thin = () => (issue.face !== 'back' ? W.tooThin(q()) : issue.fit && issue.subject?.type !== 'icon' ? W.tooThinBackFit(q()) : W.tooThinBack(q(), issue.factor));
  const why = {
    'font-load': () => W.fontLoad,
    'too-detailed': () => W.tooDetailed(W.face[issue.face] ?? W.face.front),
    'missing-chars': () => W.missingChars(labelOf(R, issue), issue.chars.slice(0, 12).join(' ')),
    'bad-char': () => (issue.kind === 'leading' ? W.leadingMark(labelOf(R, issue)) : W.badChar(labelOf(R, issue), issue.point)),
    'empty': () => W.empty,
    'too-many': () => W.tooMany(issue.max),
    'outside': () => W.outside(q()),
    'on-hole': () => W.onHole(q()),
    'overlap': () => W.overlap(q()),
    'too-thin': thin,
    'too-thin-tag': () => W.tooThinTag(issue.mm.toFixed(1)),
    'hole-wall': () => W.holeWall,
    'no-fit': () => (issue.face === 'back' ? W.noFit.back : W.noFit.front),
  }[issue.code];
  if (!why) return issue.message;
  const text = why();
  return issue.way ? W.wayOut(text, W.way[issue.way]) : text;
}

// the way out as one tap: the document change the worker names, with the page's words for its button
const actionOf = (action, S) => (action && action.template === 'name-front-phone-back' ? { ...action, label: S.worker.phoneToBack } : action);

// → the reply with `.message` on its error and on each issue (and `action.label`), the rest untouched
export function worded(reply, S) {
  if (!reply || typeof reply !== 'object') return reply;
  const out = { ...reply };
  if (reply.error) out.error = { ...reply.error, message: sentence(reply.error, S), ...(reply.error.action ? { action: actionOf(reply.error.action, S) } : {}) };
  if (Array.isArray(reply.issues)) out.issues = reply.issues.map((i) => ({ ...i, message: sentence(i, S) }));
  return out;
}
